import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/number'
import { useCountDown } from 'hooks/useCountdown'
import useModal from 'hooks/useModal'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BonusService from 'services/BonusService'
import { useTiers, useTokenUsdPrices } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useHUSDLockBonus, useUpdateHUSDBonus } from 'state/bonus/hooks'
import { useCashbackBonus } from 'state/bonus/hooks/cashbackBonus'
import { useAvailableCashbacks, useClaimCashback, useNextCaskbackTime } from 'state/cashback/hooks'
import { useProfileTier, useUserWager } from 'state/profile/hooks'
import { timeLeftUnit } from 'utils'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { minTwoDigits } from 'utils/numberHelper'
import { HunnyToast } from 'utils/toastify'
import { displayCountdownTimeUnit } from 'views/Cashback/utils'
import HUSDUnlock from 'views/HUSDUnlock'

export const useLevelUpBonus = () => {
  const userTier = useProfileTier()
  const tiers = useTiers()
  const userTierIndex = useMemo(() => tiers.findIndex((tier) => tier.id === userTier?.id), [tiers, userTier])

  const wager = useUserWager()

  const userNextTier = useMemo(
    () => tiers[userTierIndex < tiers.length ? userTierIndex + 1 : null],
    [tiers, userTierIndex],
  )
  const bonusUsdNextLevel = useMemo(
    () => getFullDisplayBalance(new BigNumber(userNextTier?.bonusUsd || 0), 0, 2)?.toString() || '',
    [userNextTier],
  )

  const wagerUsdToNextLevel = useMemo(
    () => getFullDisplayBalance(new BigNumber(wager?.target).minus(wager?.score), 0, 2)?.toString() || '',
    [wager],
  )

  return {
    wager,
    wagerUsdToNextLevel,
    bonusUsdNextLevel,
    userTier,
    userNextTier,
  }
}

export const useHusdBonus = () => {
  const { t } = useTranslation()

  const { claimableBalance, unlockableBalance } = useHUSDLockBonus()
  const { fetchHUSDBonusBalance } = useUpdateHUSDBonus()
  const [onPresentHUSDModal] = useModal(HUSDUnlock)
  const [isSubmiting, setSubmiting] = useState(false)

  const handleClaimHUSD = async () => {
    if (isDisabled) return

    setSubmiting(true)
    const claimInfo = await BonusService.getHUSDClaimableAmount().call()
    if (!claimInfo?.data) {
      setSubmiting(false)
      return
    }

    const response = await BonusService.claimHUSD(claimInfo.data.amount, claimInfo.data.wager)

    if (response.data) {
      // Ensure fetch completes before disabling loading to prevent race condition
      await fetchHUSDBonusBalance()

      HunnyToast.success(
        t('Claim successful!'),
        t('You have claimed {{amount}} HUSD. The funds have been added your account balance.', {
          amount: getFullDisplayBalance(new BigNumber(claimInfo.data.amount), 0, 18),
        }),
      )
    }

    setSubmiting(false)
  }

  useEffect(() => {
    const fetchInterval = setInterval(() => {
      fetchHUSDBonusBalance()
    }, 60 * 1000)

    return () => {
      clearInterval(fetchInterval)
    }
  }, [])

  const isDisabled = claimableBalance.eq(0) || isSubmiting

  return {
    unlockableBalance,
    claimableBalance,
    handleClaimHUSD,
    isDisabled,
    isSubmiting,
    onPresentHUSDModal,
  }
}

export const useCashbackFeature = () => {
  const { t } = useTranslation()

  const { availableCashbacks, fetchAvailableCashbacks } = useAvailableCashbacks()
  const { nextCashbackTime } = useNextCaskbackTime()
  const prices = useTokenUsdPrices()
  const { isClaiming, handleClaimCashback } = useClaimCashback()

  const { inPeriodCashbackBonus: bonus } = useCashbackBonus()

  const cashbackExpireTimeDetailRef = useCountDown(
    nextCashbackTime,
    (s, m, h, d) => `
      ${displayCountdownTimeUnit(t('Day'), 'Day', minTwoDigits(d))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('Hour'), 'Hour', minTwoDigits(h))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('Min'), 'Min', minTwoDigits(m))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('Sec'), 'Sec', minTwoDigits(s))}
    `,
    () => {},
  )

  const cashbackExpireTimePreview = useMemo(() => {
    return timeLeftUnit(nextCashbackTime)
  }, [cashbackExpireTimeDetailRef, nextCashbackTime])

  const { isSigned } = useAuth()

  const handleClaimAvailableCashback = useCallback(() => {
    if (isClaiming || !availableCashbacks?.length) return

    handleClaimCashback((isSuccessed) => {
      if (isSuccessed) {
        fetchAvailableCashbacks()
        HunnyToast.success(t('Claim successful!'))
      }
    })
  }, [isClaiming, availableCashbacks])

  useEffect(() => {
    if (isSigned) {
      fetchAvailableCashbacks()
    }
  }, [isSigned])

  const sumFiatAmountCashback = useMemo(() => {
    return availableCashbacks.reduce((state, availableCashback) => {
      return state.plus(new BigNumber(availableCashback?.value).multipliedBy(prices[availableCashback?.currency]))
    }, BIG_ZERO)
  }, [prices, availableCashbacks])

  const isAvailableCashback = sumFiatAmountCashback.gt(0)

  return {
    cashbackExpireTimeDetailRef,
    cashbackExpireTimePreview,
    sumFiatAmountCashback,
    isAvailableCashback,
    availableCashbacks,
    handleClaimCashback: handleClaimAvailableCashback,
    isDisabledByInPeriodCashback: !!bonus,
  }
}
